import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { formatNZDateLong } from "../utils/timezone";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import AddressAutocomplete from "../components/AddressAutocomplete";
import SEO from "../components/SEO";
import { 
  ArrowLeft, User, Mail, Phone, MapPin, 
  Pencil, Save, X, Sun, Moon, Search
} from "lucide-react";
import { useAuth, useTheme, API } from "../App";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";
const LIGHT_LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";

export default function CustomerAccount() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: "",
    phone: "",
    search_radius_km: 50,
    address: {
      full_address: "",
      street_number: "",
      street_name: "",
      suburb: "",
      city: "",
      postcode: "",
      latitude: null,
      longitude: null
    }
  });

  // Check authentication
  useEffect(() => {
    if (!token) {
      navigate("/customer/auth");
    }
  }, [token, navigate]);


  const fetchData = useCallback(async () => {
    try {
      const profileRes = await axios.get(`${API}/customer/profile`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });

      if (profileRes.data) {
        setCustomerProfile(profileRes.data);
        setProfileForm({
          name: profileRes.data.name || "",
          phone: profileRes.data.phone || "",
          search_radius_km: profileRes.data.search_radius_km || 50,
          address: profileRes.data.address || {
            full_address: "",
            street_number: "",
            street_name: "",
            suburb: "",
            city: "",
            postcode: "",
            latitude: null,
            longitude: null
          }
        });
      }
    } catch (error) {
      toast.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, fetchData]);

  const handleUpdateProfile = async () => {
    setSavingProfile(true);
    try {
      await axios.put(
        `${API}/customer/profile`,
        {
          name: profileForm.name,
          phone: profileForm.phone,
          search_radius_km: profileForm.search_radius_km,
          address: profileForm.address
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profile updated successfully");
      setEditingProfile(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="customer-account-page">
      <SEO page="customerAccount" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/customer/dashboard")} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Link to="/customer/dashboard">
              <img
                src={theme === "dark" ? LOGO_URL : LIGHT_LOGO_URL}
                alt="TyreMatch"
                className="h-10"
              />
            </Link>
          </div>
          <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-full">
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">My Account</h1>
          <p className="text-muted-foreground">Manage your profile and preferences</p>
        </div>

        <div className="grid gap-6">
          {/* Profile Information */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile Information
              </CardTitle>
              {!editingProfile ? (
                <Button variant="outline" size="sm" onClick={() => setEditingProfile(true)} data-testid="edit-profile-btn">
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Profile
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleUpdateProfile} disabled={savingProfile} data-testid="save-profile-btn">
                    <Save className="h-4 w-4 mr-2" />
                    {savingProfile ? "Saving..." : "Save"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => {
                    setEditingProfile(false);
                    setProfileForm({
                      name: customerProfile?.name || "",
                      phone: customerProfile?.phone || "",
                      search_radius_km: customerProfile?.search_radius_km || 50,
                      address: customerProfile?.address || {}
                    });
                  }} data-testid="cancel-edit-btn">
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {editingProfile ? (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                        data-testid="name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input
                        id="phone"
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                        placeholder="021 123 4567"
                        data-testid="phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="search_radius">Search Radius (km)</Label>
                      <Input
                        id="search_radius"
                        type="number"
                        min="5"
                        max="200"
                        value={profileForm.search_radius_km}
                        onChange={(e) => setProfileForm({ ...profileForm, search_radius_km: parseInt(e.target.value) || 50 })}
                        data-testid="radius-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        How far you're willing to travel for tyre services
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label>Address *</Label>
                      <AddressAutocomplete
                        value={profileForm.address}
                        onChange={(address) => setProfileForm({ ...profileForm, address })}
                        placeholder="Start typing your NZ address..."
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <User className="h-5 w-5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground mb-1">Name</p>
                        <p className="font-medium" data-testid="display-name">{customerProfile?.name || "Not set"}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Mail className="h-5 w-5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground mb-1">Email</p>
                        <p className="font-medium" data-testid="display-email">{customerProfile?.email || "Not set"}</p>
                        <p className="text-xs text-muted-foreground mt-1">Email cannot be changed (linked to login)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Phone className="h-5 w-5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground mb-1">Phone</p>
                        <p className="font-medium" data-testid="display-phone">{customerProfile?.phone || "Not set"}</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                      <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground mb-1">Address</p>
                        <p className="font-medium" data-testid="display-address">
                          {customerProfile?.address?.full_address || "Not set"}
                        </p>
                        {customerProfile?.address?.suburb && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            <Badge variant="secondary" className="text-xs">{customerProfile.address.suburb}</Badge>
                            {customerProfile.address.city && (
                              <Badge variant="secondary" className="text-xs">{customerProfile.address.city}</Badge>
                            )}
                            {customerProfile.address.postcode && (
                              <Badge variant="outline" className="text-xs">{customerProfile.address.postcode}</Badge>
                            )}
                          </div>
                        )}
                        {customerProfile?.address?.latitude && customerProfile?.address?.longitude && (
                          <Badge variant="outline" className="text-xs mt-2 bg-green-500/10 text-green-600 border-green-500/30">
                            Location verified
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Search className="h-5 w-5 text-muted-foreground" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground mb-1">Search Radius</p>
                        <p className="font-medium" data-testid="display-radius">
                          {customerProfile?.search_radius_km || 50} km
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Shops within this distance will receive your quote requests
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground mb-1">Member Since</p>
                        <p className="font-medium">
                          {customerProfile?.created_at 
                            ? formatNZDateLong(customerProfile.created_at) 
                            : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button variant="outline" onClick={() => navigate("/customer/dashboard")} data-testid="view-requests-btn">
                  View My Requests
                </Button>
                <Button onClick={() => navigate("/customer/dashboard?tab=new")} data-testid="new-request-btn">
                  Request New Quote
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t mt-12 py-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground text-sm">
          <p>© 2026 TyreMatch. New Zealand's smartest way to compare tyre quotes and book services.</p>
        </div>
      </footer>
    </div>
  );
}
